import { IonButton, IonChip, IonCol, IonGrid, IonIcon, IonLabel, IonModal, IonRow } from "@ionic/react"
import { useEffect, useMemo, useState } from "react"
import { API_URL } from "../../../actions/settings"
import './SuccessStoriesTable.css'
import { chevronDownCircle, chevronUpCircle, imageOutline, pencil, trashBin } from "ionicons/icons"
import NotificationPopUp from "../../extras/NotificationPopUp"
import FilterSuccessStoriesTable from "./FilterSuccessStoriesTable"
import SuccessStoryForm from "./forms/SuccessStoryForm"
import AIModal from "../../elements/AIModal"
import { useAppSelector } from "../../../Hooks"
import { selectUserGroups } from "../../../reducers/UserReducer"

type Keyword = {
    id:string,
    keyword:string,
    abbreviation?:string
}

type Story = {
    id:string,
    name?:string|null,
    headline?:string|null,
    description?:string|null,
    full_story?:string|null,
    image_url?:string|null,
    video_url?:string|null,
    market_segment:string,
    market_sub_segment?:string|null,
    location:{name:string,id:string}|null,
    representative:{name:string}|null,
    keywords:Keyword[],
    date:string
}

interface Props{
    stories:Story[],
    setStories:any
}

type SortKey = 'name' | 'location' | 'market_segment' | 'date'

const SORT_OPTIONS:{key:SortKey,label:string}[] = [
    {key:'name',label:'Name'},
    {key:'location',label:'Location'},
    {key:'market_segment',label:'Market Segment'},
    {key:'date',label:'Date Added'},
]

async function deleteStory(id:any,token:any) {

  return fetch(`${API_URL}/success_story/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization':token
    }

  })
    .then(data => data.json())
 }

const segmentLabel = (story:Story) =>
    story.market_sub_segment ? `${story.market_segment} (${story.market_sub_segment})` : (story.market_segment || '')

const sortValue = (story:Story, key:SortKey) => {
    switch(key){
        case 'name': return (story.name || story.headline || '').toLowerCase()
        case 'location': return (story.location?.name || '').toLowerCase()
        case 'market_segment': return segmentLabel(story).toLowerCase()
        default: return story.date || ''
    }
}

const searchText = (story:Story) => [
    story.name, story.headline, story.description, story.full_story,
    story.location?.name, segmentLabel(story), story.representative?.name, story.date,
    ...(story.keywords || []).flatMap(k => [k.keyword, k.abbreviation])
].filter(Boolean).join(' ').toLowerCase()

const embedUrl = (url:string) => {
    const youtube = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/)
    if(youtube) return `https://www.youtube.com/embed/${youtube[1]}`
    const vimeo = url.match(/vimeo\.com\/(\d+)/)
    if(vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`
    return null
}

const VideoPlayer = ({url}:{url:string}) => {
    const embed = embedUrl(url)
    if(embed){
        return <iframe className="story-video" src={embed} title="Story video" allowFullScreen />
    }
    if(/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)){
        return <video className="story-video" src={url} controls />
    }
    return <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
}

const KeywordChips = ({keywords}:{keywords:Keyword[]}) => (
    <div className="story-keywords">
        {(keywords || []).map(k =>
            <span className="keyword-pill" key={k.id} title={k.keyword}>{k.abbreviation || k.keyword}</span>
        )}
    </div>
)

const SuccessStoriesTable: React.FC<Props> = ({stories,setStories}) => {

    const user_groups = useAppSelector(selectUserGroups)
    const canEdit = user_groups.includes('SuccessStoriesEditDelete')
    const [opened,setOpened] = useState(false)
    const [openEditModal,setOpenEditModal] = useState(false)
    const [toDeleteID,setToDeleteID] = useState<any>()
    const [toEditID,setToEditID] = useState<any>(null)
    const [viewStory,setViewStory] = useState<Story|null>(null)
    const [filter, setFilter] = useState("")
    const [sortKey, setSortKey] = useState<SortKey>('date')
    const [sortDirection, setSortDirection] = useState<'asc'|'desc'>('desc')

    useEffect(()=>{
        if(!openEditModal){
            setToEditID(null)
        }
    },[openEditModal])

    const rows = useMemo(()=>{
        const term = filter.trim().toLowerCase()
        const filtered = (stories || []).filter(story => !term || searchText(story).includes(term))
        return filtered.sort((a,b)=>{
            const valA = sortValue(a,sortKey)
            const valB = sortValue(b,sortKey)
            const res = valA > valB ? 1 : valA < valB ? -1 : 0
            return sortDirection === 'asc' ? res : -res
        })
    },[stories,filter,sortKey,sortDirection])

    const handleSort = (key:SortKey) => {
        if(key === sortKey){
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        }else{
            setSortKey(key)
            setSortDirection('asc')
        }
    }

    const handleDelete = (id:any) =>{
        setToDeleteID(id)
        setOpened(true)
    }
    const handleEdit = (id:any) =>{
        setViewStory(null)
        setToEditID(id)
        setOpenEditModal(true)
    }

    const confirmDelete = async () =>{
        const token = localStorage.getItem('token')
        const res = await deleteStory(toDeleteID,`Token ${token!.slice(1,-1)}`)

        if (res.status === "success"){
            setStories(stories.filter((story:any) => story.id !== toDeleteID))
            setViewStory(null)
            NotificationPopUp('Success','Story Deleted')
        }
        else{
            NotificationPopUp('error',res.detail||res.ERROR,res.error)
        }
        setToDeleteID('')
        setOpened(false)
    }

    return (
        <div id='success-stories-table-wrapper'>
            <SuccessStoryForm opened={openEditModal} setShowModal={setOpenEditModal} setData={setStories} edit={toEditID} setEdit={setToEditID} />
            <FilterSuccessStoriesTable filter={filter} setFilter={setFilter} />

            <div className="story-sort">
                <span className="story-sort-label">Sort by</span>
                {SORT_OPTIONS.map(option =>
                    <IonChip key={option.key} outline={sortKey !== option.key} onClick={()=>handleSort(option.key)}>
                        <IonLabel>{option.label}</IonLabel>
                        {sortKey === option.key && <IonIcon icon={sortDirection === 'asc' ? chevronUpCircle : chevronDownCircle} />}
                    </IonChip>
                )}
                <span className="story-count">{rows.length} {rows.length === 1 ? 'story' : 'stories'}</span>
            </div>

            <div className="stories-table">
                <div className="stories-row stories-head">
                    <div>Story</div>
                    <div>Location</div>
                    <div>Keywords</div>
                    <div>Representative</div>
                    <div>Added</div>
                    <div></div>
                </div>
                {rows.length === 0 && <div className="stories-empty">No success stories found</div>}
                {rows.map(story =>
                    <div key={story.id} className="stories-row stories-body" onClick={()=>setViewStory(story)}>
                        <div className="story-main">
                            {story.image_url
                                ? <img className="story-thumb" src={story.image_url} alt={story.name || 'Story'} loading="lazy" />
                                : <div className="story-thumb story-thumb-empty"><IonIcon icon={imageOutline} /></div>}
                            <div className="story-text">
                                <div className="story-name">{story.name || 'Untitled Story'}</div>
                                {story.headline && <div className="story-headline">{story.headline}</div>}
                                {story.description && <div className="story-description">{story.description}</div>}
                            </div>
                        </div>
                        <div>
                            <div className="story-name">{story.location?.name}</div>
                            <div className="story-sub">{segmentLabel(story)}</div>
                        </div>
                        <div><KeywordChips keywords={story.keywords} /></div>
                        <div>{story.representative?.name}</div>
                        <div>{story.date}</div>
                        <div className="story-actions" onClick={e=>e.stopPropagation()}>
                            {canEdit && <>
                                <IonButton color="medium" size="small" onClick={()=>handleEdit(story.id)}>
                                    <IonIcon src={pencil} slot="icon-only"/>
                                </IonButton>
                                <IonButton color="danger" size="small" onClick={()=>handleDelete(story.id)}>
                                    <IonIcon src={trashBin} slot="icon-only"/>
                                </IonButton>
                            </>}
                        </div>
                    </div>
                )}
            </div>

            <AIModal opened={viewStory !== null} setOpened={(open:boolean)=>{if(!open) setViewStory(null)}} title={viewStory?.name || 'Success Story'}>
                {viewStory && <div className="story-detail">
                    {viewStory.image_url && <img className="story-detail-image" src={viewStory.image_url} alt={viewStory.name || 'Story'} />}
                    {viewStory.headline && <h2 className="story-detail-headline">{viewStory.headline}</h2>}
                    <div className="story-detail-meta">
                        <div><span>Location</span>{viewStory.location?.name}</div>
                        <div><span>Market Segment</span>{segmentLabel(viewStory)}</div>
                        <div><span>Representative</span>{viewStory.representative?.name}</div>
                        <div><span>Date Added</span>{viewStory.date}</div>
                    </div>
                    {viewStory.keywords?.length > 0 && <KeywordChips keywords={viewStory.keywords} />}
                    {viewStory.description && <>
                        <h3>Summary</h3>
                        <p className="text-footer-blue">{viewStory.description}</p>
                    </>}
                    {viewStory.full_story && <>
                        <h3>Full Story</h3>
                        <p className="story-full text-footer-blue">{viewStory.full_story}</p>
                    </>}
                    {viewStory.video_url && <>
                        <h3>Video</h3>
                        <VideoPlayer url={viewStory.video_url} />
                    </>}
                    {canEdit && <div className="story-detail-actions">
                        <IonButton size="small" onClick={()=>handleEdit(viewStory.id)}>Edit</IonButton>
                        <IonButton size="small" color="danger" onClick={()=>handleDelete(viewStory.id)}>Delete</IonButton>
                    </div>}
                </div>}
            </AIModal>

            <IonModal isOpen={opened} onDidDismiss={()=>setOpened(false)} className="confirmDelete">
                <IonGrid>
                    <IonRow className="ion-justify-content-center">
                        <IonCol className="delete-warning">
                            Are You Sure You Want To Delete This?
                        </IonCol>
                    </IonRow>
                    <IonRow className="ion-justify-content-center">
                        <IonButton color="danger" onClick={()=> confirmDelete()} >Delete</IonButton>
                        <IonButton color="medium" onClick={ ()=> setOpened(false)} >Cancel</IonButton>
                    </IonRow>
                </IonGrid>
            </IonModal>
        </div>
    )
}

export default SuccessStoriesTable
